import {
  Dialog,
  DialogProps,
  DialogHeader,
  DialogContent,
  DialogFooter,
  Button,
  Box,
  FormLabel,
  Stack,
  InputGroup,
  InputMessage,
  Text,
  InputText,
} from "@aksara-ui/react";
import { InputSelect } from "components/Form/InputSelect";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeUser, updateUser } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GridWrapper = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

interface Props {
  userId: string;
}

interface ValueSchema {
  label: string;
  value: string | number;
}

const initialValues = {
  name: "",
  email: "",
  phoneNumber: "",
  password: "",
  role: "",
  phoneIsVerified: "",
  emailIsVerified: "",
  greeting: "",
  birthdate: "",
  address: "",
  tenantId: "",
};

const UserFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  userId,
}) => {
  const isAddMode = !userId;
  const dispatch = useDispatch();
  const formikRef = useRef<FormikValues>() as any;

  const [role, setRole] = useState<ValueSchema | null>(null);
  const [greeting, setGreeting] = useState<ValueSchema | null>(null);
  const [tenant, setTenant] = useState<ValueSchema | null>(null);
  const [phoneIsVerified, setPhoneIsVerified] = useState<ValueSchema | null>(
    null
  );
  const [emailIsVerified, setEmailIsVerified] = useState<ValueSchema | null>(
    null
  );

  const { isFormLoading, users } = useSelector(
    (state: any) => state.userReducer
  );

  const [roleItems] = React.useState([
    { label: "Admin", value: "admin" },
    { label: "Tenant", value: "tenant" },
  ]);
  const [greetingItems] = React.useState([
    { label: "Mr", value: "mr" },
    { label: "Mrs", value: "mrs" },
    { label: "Miss", value: "miss" },
    { label: "Ms", value: "ms" },
  ]);
  const [tenantItems] = React.useState([
   { label: "Ancol Points", value: 3 },
  ]);
  const [phoneIsVerifiedItems] = React.useState([
    { label: "No", value: 0 },
    { label: "Yes", value: 1 },
  ]);
  const [emailIsVerifiedItems] = React.useState([
    { label: "No", value: 0 },
    { label: "Yes", value: 1 },
  ]);

  const handleCloseModal = () => {
    onClose && onClose();
    setRole(null);
    setGreeting(null);
    setTenant(null);
    setPhoneIsVerified(null);
    setEmailIsVerified(null);
  };

  const handleSubmit = (values: any) => {
    const {
      name,
      email,
      phoneNumber,
      password,
      phoneIsVerified,
      emailIsVerified,
      role,
      greeting,
      birthdate,
      address,
      tenantId,
    } = values;

    isAddMode
      ? dispatch(
          storeUser(
            {
              name,
              email,
              phoneNumber,
              password,
              phoneIsVerified,
              emailIsVerified,
              role,
              greeting,
              birthdate,
              address,
              tenantId,
            },
            handleCloseModal
          ) as any
        )
      : dispatch(
          updateUser(
            userId,
            {
              name,
              email,
              phoneNumber,
              password,
              phoneIsVerified,
              emailIsVerified,
              role,
              greeting,
              birthdate,
              address,
              tenantId,
            },
            handleCloseModal
          ) as any
        );
  };

  useEffect(() => {
    if (!isAddMode) {
      const userDetail = users.find(
        (user: any) => user.id === parseInt(userId)
      );

      if (formikRef.current) {
        formikRef.current.setFieldValue("name", userDetail.name);
        formikRef.current.setFieldValue("email", userDetail.email);
        formikRef.current.setFieldValue("phoneNumber", userDetail.phone_number);
        // formikRef.current.setFieldValue("password", userDetail.password);
        formikRef.current.setFieldValue("role", userDetail.role);
        formikRef.current.setFieldValue("greeting", userDetail.greeting);
        formikRef.current.setFieldValue("birthdate", userDetail.birthdate);
        formikRef.current.setFieldValue("address", userDetail.address);
        formikRef.current.setFieldValue("tenantId", userDetail.tenant_id);
        formikRef.current.setFieldValue(
          "phoneIsVerified",
          userDetail.phone_is_verified
        );
        formikRef.current.setFieldValue(
          "emailIsVerified",
          userDetail.email_is_verified
        );

        roleItems.map((item) =>
          item.value === userDetail.role ? setRole(item) : null
        );
        greetingItems.map((item) =>
          item.value === userDetail.greeting ? setGreeting(item) : null
        );
        tenantItems.map((item) =>
          item.value.toString() === userDetail.tenant_id
            ? setTenant(item)
            : null
        );
        phoneIsVerifiedItems.map((item) =>
          item.value === userDetail.phone_is_verified
            ? setPhoneIsVerified(item)
            : null
        );
        emailIsVerifiedItems.map((item) =>
          item.value === userDetail.email_is_verified
            ? setEmailIsVerified(item)
            : null
        );
      }
    }
  }, [isAddMode, users, userId]);

  const validationSchema = yup.object({
    name: yup.string().required("Name is required field"),
    email: yup
      .string()
      .email("Field must be Email format")
      .required("Email is required field"),
    phoneNumber: yup
      .number()
      .typeError("That doesn't look like a phone number")
      .positive("A phone number can't start with a minus")
      .integer("A phone number can't include a decimal point")
      .required("Phone Number is required field"),
    password: yup
      .string()
      .when([], () =>
        isAddMode
          ? yup
              .string()
              .min(8, "Password must be more than 8 characters")
              .required("Password is required field")
          : yup
              .string()
              .min(8, "Password must be more than 8 characters")
              .notRequired()
      ),
    role: yup.string().required("Role is required field"),
    phoneIsVerified: yup.string(),
    emailIsVerified: yup.string(),
    greeting: yup.string().required("Greeting is required field"),
    birthdate: yup.string().required("Birthdate is required field"),
    address: yup.string().required("Address is required field"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>
                  {isAddMode ? "Add User" : "Update User"}
                </Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="name">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="name">Name</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <GridWrapper>
                    <Field name="email">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="email">Email</FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="text"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                    <Field name="phoneNumber">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="phoneNumber">
                            Phone Number
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputText
                              type="tel"
                              size={40}
                              errors={!!meta.error && meta.touched}
                              {...field}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  </GridWrapper>
                  <Field name="password">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="password">Password</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="password"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <GridWrapper>
                    <Field name="phoneIsVerified">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="phoneIsVerified">
                            Phone Verified
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={phoneIsVerified}
                              width={"100%"}
                              placeholder="Select Phone Verified"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setPhoneIsVerified(selectedItem);
                                  setFieldValue(
                                    "phoneIsVerified",
                                    selectedItem.value
                                  );
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={phoneIsVerifiedItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                    <Field name="emailIsVerified">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="emailIsVerified">
                            Email Verified
                          </FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={emailIsVerified}
                              width={"100%"}
                              placeholder="Select Email Verified"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setEmailIsVerified(selectedItem);
                                  setFieldValue(
                                    "emailIsVerified",
                                    selectedItem.value
                                  );
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={emailIsVerifiedItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  </GridWrapper>
                  <GridWrapper>
                    <Field name="role">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="role">Role</FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={role}
                              width={"100%"}
                              placeholder="Select Role"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setRole(selectedItem);
                                  setFieldValue("role", selectedItem.value);
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={roleItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                    <Field name="greeting">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="greeting">Greeting</FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={greeting}
                              width={"100%"}
                              placeholder="Select Greeting"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setGreeting(selectedItem);
                                  setFieldValue("greeting", selectedItem.value);
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={greetingItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  </GridWrapper>

                  <Field name="tenantId">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="tenantId">Tenant</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputSelect
                            selectedItem={tenant}
                            width={"100%"}
                            placeholder="Select Tenant"
                            handleSelectedItemChange={({ selectedItem }) => {
                              if (selectedItem) {
                                setTenant(selectedItem);
                                setFieldValue("tenantId", selectedItem.value);
                              }
                            }}
                            itemToString={(item) =>
                              item ? `${item.label}` : ""
                            }
                            itemValue={(item) => (item ? `${item.value}` : "")}
                            items={tenantItems}
                            size={"lg"}
                            borderRadius={"6px"}
                            errors={!!meta.error && meta.touched}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="birthdate">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="birthdate">Birthdate</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="date"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="address">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="address">Address</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    {isAddMode ? "Add" : "Update"}
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default UserFormModal;
